import type { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { WorkingTimeModel } from '../data/databaseSchema';
import { workingTimeModelService } from '../services/workingTimeModelService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { WorkingTimeModelDrawer } from './WorkingTimeModelDrawer';

type DetailPanelMode = 'empty' | 'view' | 'edit' | 'add';

interface WorkingTimeModelListProps {
  onEdit?: (model: WorkingTimeModel) => void;
  onDelete?: (modelId: string) => void;
  onAdd?: () => void;
  onView?: (model: WorkingTimeModel) => void;
}

export const WorkingTimeModelList: React.FC<WorkingTimeModelListProps> = ({
  onEdit,
  onDelete,
  onAdd,
  onView,
}) => {
  // State for working time models data
  const [workingTimeModels, setWorkingTimeModels] = useState<WorkingTimeModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch working time models on component mount
  useEffect(() => {
    const fetchWorkingTimeModels = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await workingTimeModelService.getWorkingTimeModels();
        setWorkingTimeModels(response);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkingTimeModels();
  }, []);

  const refetch = () => {
    const fetchWorkingTimeModels = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await workingTimeModelService.getWorkingTimeModels();
        setWorkingTimeModels(response);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkingTimeModels();
  };
  const gridRef = useRef<AgGridReact>(null);
  const [selectedModels, setSelectedModels] = useState<WorkingTimeModel[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const justSavedRef = useRef(false);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedModel, setSelectedModel] = useState<WorkingTimeModel | null>(null);
  const [editingModel, setEditingModel] = useState<WorkingTimeModel | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const handleAddClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    setPanelMode('empty');
    setEditingModel(null);
  }, []);

  const handleEditClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingModel(null);
      return;
    }
    setPanelMode('view');
    setEditingModel(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedModel(null);
  }, []);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<WorkingTimeModel>) => {
      if (!event.data) return;
      setSelectedModel(event.data);
      setPanelMode('view');
      setEditingModel(null);
      onView?.(event.data);
    },
    [onView]
  );

  const handleDelete = useCallback(
    async (model: WorkingTimeModel) => {
      if (
        !onDelete ||
        !window.confirm(`Are you sure you want to delete working time model ${model.name}?`)
      ) {
        return;
      }
      setIsDeleting(model.id);
      try {
        await workingTimeModelService.deleteWorkingTimeModel(model.id);
        onDelete(model.id);
        refetch();
        setSelectedModel(null);
        setPanelMode('empty');
      } catch (error) {
        alert(
          `Failed to delete working time model: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      } finally {
        setIsDeleting(null);
      }
    },
    [onDelete]
  );

  // Column definitions for the working time model grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 100,
        pinned: 'left',
        lockPosition: 'left',
        headerClass: agGridCSSClasses.modernHeader,

        cellClass: agGridCSSClasses.modernCell,
      },
      {
        headerName: 'FTE',
        field: 'fte',
        width: 80,
        minWidth: 70,
        maxWidth: 90,
        editable: false,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader} ${agGridCSSClasses.readOnlyHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: `${agGridCSSClasses.modernCell} ${agGridCSSClasses.readOnlyCell}`,
        cellStyle: { textAlign: 'center' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return '';
          return typeof params.value === 'number' ? params.value.toFixed(2) : params.value;
        },
      },
      {
        headerName: 'Total',
        field: 'hours',
        width: 90,
        minWidth: 80,
        editable: false,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader} ${agGridCSSClasses.readOnlyHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: `${agGridCSSClasses.modernCell} ${agGridCSSClasses.readOnlyCell}`,
        cellStyle: { textAlign: 'center' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}h`;
        },
      },
      {
        headerName: 'Mon',
        field: 'monday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
      {
        headerName: 'Tue',
        field: 'tuesday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
      {
        headerName: 'Wed',
        field: 'wednesday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
      {
        headerName: 'Thu',
        field: 'thursday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
      {
        headerName: 'Fri',
        field: 'friday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
      {
        headerName: 'Sat',
        field: 'saturday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
      {
        headerName: 'Sun',
        field: 'sunday',
        flex: 1,
        width: 70,
        headerClass: `${agGridCSSClasses.modernHeader} ${agGridCSSClasses.rightAlignHeader}`,
        headerStyle: { textAlign: 'right' },
        cellClass: agGridCSSClasses.modernCell,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return `${params.value}`;
        },
      },
    ],
    [onView]
  );

  // Search functionality
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (gridApi) {
        gridApi.setGridOption('quickFilterText', searchValue);
      }
    },
    [gridApi]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', '');
    }
  }, [gridApi]);

  // Export functionality
  const handleExportCSV = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'working-time-models.csv',
        columnKeys: [
          'id',
          'fte',
          'hours',
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday',
        ],
      });
    }
  }, [gridApi]);

  // Grid event handlers
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    // Data rendered
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    // Row data updated
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedModels(selectedData);
    }
  }, [gridApi]);

  // Grid options
  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onRowDataUpdated: handleRowDataUpdated,
    onSelectionChanged: handleSelectionChanged,
    onRowClicked: handleRowClicked,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    quickFilterText: searchTerm,
    rowNumbers: { minWidth: 25 },
    alternateBackground: true,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
  };

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4 text-text-secondary">
          <Spinner className="size-10" />
          <p>Loading working time models...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4  p-8 text-danger-600">
        <p>Error loading working time models: {error.message}</p>
        <Button type="button" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'add' ? (
          <WorkingTimeModelDrawer
            isOpen={true}
            onClose={handleAddClose}
            onSave={(model) => {
              onEdit?.(model);
            }}
            onEntrySaved={() => {
              justSavedRef.current = true;
              setPanelMode('empty');
              setSelectedModel(null);
              refetch();
            }}
            model={null}
            isEdit={false}
          />
        ) : panelMode === 'edit' && editingModel ? (
          <WorkingTimeModelDrawer
            isOpen={true}
            onClose={handleEditClose}
            onSave={(model) => {
              onEdit?.(model);
              setSelectedModel(model);
            }}
            onEntrySaved={() => {
              justSavedRef.current = true;
              setPanelMode('view');
              setEditingModel(null);
              refetch();
            }}
            model={editingModel}
            isEdit
          />
        ) : panelMode === 'view' && selectedModel ? (
          <WorkingTimeModelDrawer
            key={selectedModel.id}
            isOpen={true}
            readOnly
            model={selectedModel}
            onClose={handleViewClose}
            onSave={() => {}}
            onEditRequest={
              onEdit
                ? () => {
                    setEditingModel(selectedModel);
                    setPanelMode('edit');
                    onEdit(selectedModel);
                  }
                : undefined
            }
            onDeleteRequest={onDelete ? () => void handleDelete(selectedModel) : undefined}
            isDeleteDisabled={isDeleting === selectedModel.id}
          />
        ) : null
      }
      list={
        <div className="flex min-h-0 flex-1 flex-col p-2">
          <div className="mb-5 flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative  bg-white">
                <Input
                  type="text"
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pr-8"
                />
                {searchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </div>

              {selectedModels.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600">
                  {selectedModels.length} selected
                </div>
              )}

              <Button type="button" variant="secondary" onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>

            {onAdd && (
              <Button
                type="button"
                onClick={() => {
                  setEditingModel(null);
                  setPanelMode('add');
                  onAdd();
                }}
              >
                <PlusIcon data-icon="inline-start" />
                Add Working Time Model
              </Button>
            )}
          </div>

          {/* AG Grid */}
          <style>{agGridThemeStyles}</style>
          {workingTimeModels.length > 0 && (
            <div className="ag-theme-material min-h-0 flex-1" style={agGridSettingsContainerStyles}>
              <AgGridReact
                ref={gridRef}
                rowData={workingTimeModels}
                columnDefs={columnDefs}
                defaultColDef={agGridDefaultColDef}
                gridOptions={gridOptions}
                animateRows={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
              />
              <div>
                <p className="p-2 text-sm text-text-secondary">
                  {workingTimeModels.length} working time model
                  {workingTimeModels.length !== 1 ? 's' : ''} found
                </p>
              </div>
              {isLoading && (
                <div className=" items-center gap-2 px-8 py-4 text-sm font-medium text-text-secondary">
                  <Spinner className="size-4" />
                  Loading working time models...
                </div>
              )}
            </div>
          )}
          {/* Empty state */}
          {workingTimeModels.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="text-5xl text-text-muted">⏰</div>
              <div className="text-lg font-medium text-text-secondary">
                No working time models found
              </div>
              <div className="text-sm text-text-muted">
                Add your first working time model to get started
              </div>
              {onAdd && (
                <Button
                  type="button"
                  className="mt-2"
                  onClick={() => {
                    setEditingModel(null);
                    setPanelMode('add');
                    onAdd();
                  }}
                >
                  <PlusIcon data-icon="inline-start" />
                  Add Working Time Model
                </Button>
              )}
            </div>
          )}
        </div>
      }
    />
  );
};
